import React, {useState, useCallback, useEffect, useMemo} from 'react'
import {useTranslation} from 'react-i18next';
import SMCModalButtonContainer from '../../components/core/SMCModal/SMCModalButtonContainer';
import SMCModalBody from '../../components/core/SMCModal/SMCModalBody';
import SMCButton from '../../components/Simple/SMCButton';
import SMCModal from '../../components/core/SMCModal';
import Api from '../../api/SMPFetchHelper';
import SMCTable from '../../components/core/SMCTable/SMCTable';
import appCategoryIcon from '../../assets/images/app_category.png';
import appControlIcon from '../../assets/images/app_control_16.png';
import SMCTextInput from '../../components/Simple/SMCTextInput';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useSMCErrorHandler} from '../../utils/errorUtils';

const appiImages = require.context('../../assets/images/appi', true);


function getAppiImageById(id) {
    const iconId = id.substr('app_'.length); // All appi ids start with app_, we onlt need the id number
    let imageSrc = '';
    try {
        // Try reading the image from appi image context, if it's not there, an exception will be thrown
        imageSrc = appiImages(`./${iconId}_sml.png`).default;
    } catch (e) {
        // If image not found from appi image context, use this url source
        imageSrc = 'https://sc1.checkpoint.com/za/images/facetime/small_png/' + iconId + "_sml.png";
    }
    return imageSrc;
}

const appiNoIconFoundSet = new Set();

// Is the image is not found, change the icon to appControlIcon (default appi image)
function appiImageErrorHandler(e, id) {
    e.target.onerror = null;
    e.target.src = appControlIcon;
    appiNoIconFoundSet.add(id);
}

const VersionCell = React.memo(({value}) => {
    const {t} = useTranslation();
    switch (value) {
        case "VER_ALL":
            return <span>{t('SMC_APPI_VER_ALL')}</span>;
        case "VER_3":
            return <span>{t('SMC_APPI_VER_3')}</span>;
        case "VER_4":
            return <span>{t('SMC_APPI_VER_4')}</span>;
        default:
            return undefined;
    }
});
const NameCell = ({value, object}) => {
    if (object.tbl === 'appiDb' || object.tbl === 'appiDbHttps') {
        return (
            <span>
                <img
                    style={{verticalAlign: 'middle', margin: '-2px 5px 0px 0px', width: '16px'}}
                    // If the id has no icon found, show appControlIcon
                    src={appiNoIconFoundSet.has(object.id) ? appControlIcon : getAppiImageById(object.id)}
                    // If the id has no icon found, we show appControlIcon and don't need an error handler
                    onError={appiNoIconFoundSet.has(object.id) ? undefined : e => appiImageErrorHandler(e, object.id)}
                    alt=""
                />
                {value}
            </span>
        )
    } else {
        // Category has only one icon
        return <span><img style={{verticalAlign: 'middle', margin: '-2px 5px 0px 0px', width: '16px'}}
                          src={appCategoryIcon} alt=""/>{value}</span>
    }
};

/**
 * Appi editor modal
 * @param {Object} params
 * @param {Boolean} params.isOpen Is window open
 * @param {[String]} params.deafultSelected Default selected objects
 * @param {Function} params.onRequestClose Window close function
 * @param {Function} params.onSuccess Success callback
 * @param {('APPI'|'SSL_INSPECTION')} params.appType Is this an appi editor or ssl inspection editor
 * @param {Boolean} [params.selectMultiple] Is multiple selection allowed. Default is true
 */
function AppiEditorModal({
                             isOpen,
                             deafultSelected,
                             onRequestClose,
                             onSuccess,
                             appType,
                             selectMultiple = true,
                             setAppiDataValue
                         }) {
    const {t} = useTranslation();
    const {getErrorMessage} = useSMCErrorHandler();

    const modelWidth = useMemo(() => {
        if (appType === 'SSL_INSPECTION')
            return 600;
        else return 900;
    }, [appType])

    const tableWidth = useMemo(() => {
        if (appType === 'SSL_INSPECTION')
            return 560;
        else return 840;
    }, [appType])


    const appiTableColumns = useMemo(() => {
        if (appType === 'SSL_INSPECTION')
            return [
                {id: 'name', accessor: 'name', header: t('SMP.NAME'), width: 400, Cell: NameCell}
            ];
        else if (appType === "ACCESS_POLICY")
            return [
                {id: 'name', accessor: 'name', header: t('SMP.NAME'), width: 400, Cell: NameCell},
                {id: 'categories', accessor: 'categories', header: t('SMP.CATEGORIES'), width: 400}
            ]
        else return [
                {id: 'name', accessor: 'name', header: t('SMP.NAME'), width: 400, Cell: NameCell},
                {id: 'version', accessor: 'version', header: t('SMP.VERSION'), width: 130, Cell: VersionCell},
                {id: 'categories', accessor: 'categories', header: t('SMP.CATEGORIES'), width: 270}
            ]
    }, [appType, t])

    const [selectedObjects, setSelectedObjects] = useState([]);
    const [isTableDataLoading, setIsTableDataLoading] = useState(false);
    const [appiTableData, setAppiTableData] = useState([]);
    const [errorMessage, setErrorMessage] = useState(null);

    const [searchText, setSearchText] = useState('');
    const [filteredData, setFilteredData] = useState([]);

    const handleTableSelect = useCallback((selectedObjectsIds) => {
        if (!selectMultiple && (selectedObjectsIds.length > 0))
            setSelectedObjects([selectedObjectsIds[selectedObjectsIds.length - 1]]);
        else
            setSelectedObjects(selectedObjectsIds)
    }, [selectMultiple])

    const loadData = useCallback(() => {
        setIsTableDataLoading(true);
        Api.getAppiOtherApplicationsList("VER_ALL")
            .then((resp) => {
                let appiData = [];
                if (appType === 'APPI') {
                    appiData = resp.data.data;
                } else if (appType === 'ACCESS_POLICY') {
                    appiData = resp.data.data.filter(appi => appi.version === 'VER_4' || appi.version === 'VER_ALL');
                } else if (appType === 'SSL_INSPECTION') {
                    appiData = resp.data.data.filter(appi => appi.tbl === 'appCategory');
                } else {
                    appiData = resp.data.data;
                }
                setAppiTableData(appiData);
                setFilteredData(appiData);
                setAppiDataValue(resp.data.data);
            })
            .catch(e => {
                const errorMessage = getErrorMessage(e);
                setErrorMessage(errorMessage);
            })
            .finally(() => {
                setIsTableDataLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appType, getErrorMessage]);

    useEffect(() => {
            loadData();
    }, [loadData]);

    useEffect(() => {
        if (isOpen) {
            setSelectedObjects(deafultSelected || []);
            setSearchText('');
        }
    }, [isOpen, deafultSelected]);

    // Update filtered data on search text change
    useEffect(() => {
        if (!searchText) {
            setFilteredData(appiTableData);
        } else {
            setFilteredData(
                appiTableData.filter(appiObj => appiObj.name.toUpperCase().includes(searchText.toUpperCase()))
            );
        }
    }, [searchText, appiTableData]);

    const handleApply = useCallback(() => {
        onSuccess(selectedObjects);
    }, [selectedObjects, onSuccess]);

    return (
        <SMCModal
            title={t('SMP.PLAN.APP_URLS.BLOCK.LINE1')}
            height={605}
            width={modelWidth}
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            errorMessage={errorMessage}
            reloadData={loadData}
        >
            <SMCModalBody lineHeight="22px" style={{paddingTop: '5px'}}>
                <SMCTextInput
                    suffixIcon="search"
                    allowClearAll
                    placeholder={t('SMP.SEARCH')}
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                />
                <SMCTable
                    loading={isTableDataLoading}
                    content={filteredData}
                    columns={appiTableColumns}
                    width={tableWidth}
                    height={345}
                    selectedObjects={selectedObjects}
                    setSelectedObjects={handleTableSelect}
                    hasCheckAll={selectMultiple}
                    rowIdSelector={obj => obj.id}
                    showSelectedBox={true}
                    selectedBoxNameSelector={obj => obj.name}
                />
            </SMCModalBody>
            <SMCModalButtonContainer>
                <SMCButton onClick={handleApply}>
                    <FontAwesomeIcon style={{marginTop: '3px', marginRight: '5px'}} color="green" icon="check"/>
                    {t('SMC_GEN_APPLY')}
                </SMCButton>
                <SMCButton onClick={onRequestClose}>
                    <FontAwesomeIcon style={{marginTop: '3px', marginRight: '5px'}} color="grey" icon="times"/>
                    {t('SMC_GEN_CANCEL')}
                </SMCButton>
            </SMCModalButtonContainer>
        </SMCModal>
    );
}

export default AppiEditorModal;